import {
  DOMParser as PMDomParser,
  DOMSerializer,
  Node as ProsemirrorNode,
} from 'prosemirror-model';

import {
  basicSchemaFactory,
  defaultSchemaFactory,
  jobHighlightSchemaFactory,
  richSchemaFactory,
  RtSchema,
  textingSchemaFactory,
} from './schema';

/**
 * THIS MAP IS USED ON BACKEND
 */
export const SCHEMAS = {
  [RtSchema.Default]: defaultSchemaFactory,
  [RtSchema.Basic]: basicSchemaFactory,
  [RtSchema.Texting]: textingSchemaFactory,
  [RtSchema.JobHighlight]: jobHighlightSchemaFactory,
  [RtSchema.Rich]: richSchemaFactory,
  [RtSchema.JobDescription]: richSchemaFactory,
  [RtSchema.JobRequirements]: richSchemaFactory,
  [RtSchema.TalentPoolDescription]: basicSchemaFactory,
  [RtSchema.OfferLetterDescription]: richSchemaFactory,
};

/**
 * THIS FUNCTION IS EXECUTED ON BACKEND
 */
export const htmlToJsonFactory =
  (getDocument: () => Document, parseHtml: (html: string) => Node) =>
  <S extends RtSchema>(
    html: string | Node,
    rtSchema: S,
    options?: Parameters<(typeof SCHEMAS)[S]>[1],
  ): { doc: Record<string, any> } | null => {
    const htmlDoc = typeof html === 'string' ? parseHtml(`<div>${html}</div>`) : html;
    if (!htmlDoc) return null;

    const parser = PMDomParser.fromSchema(SCHEMAS[rtSchema](getDocument(), options));
    const pmDoc = parser.parse(htmlDoc);
    return { doc: pmDoc.toJSON() };
  };

/**
 * THIS FUNCTION IS EXECUTED ON BACKEND
 */
export const jsonToHtmlFactory =
  (getDocument: () => Document) =>
  <S extends RtSchema>(
    json: { doc: Record<string, any> },
    rtSchema: S,
    options?: Parameters<(typeof SCHEMAS)[S]>[1],
  ): string => {
    const schema = SCHEMAS[rtSchema](getDocument(), options);
    const document = getDocument();

    const contentNode = ProsemirrorNode.fromJSON(schema, json.doc);
    const serializer = DOMSerializer.fromSchema(schema);
    const domFragment = serializer.serializeFragment(contentNode.content, { document });
    const div = document.createElement('div');
    div.appendChild(domFragment);
    // eslint-disable-next-line no-restricted-properties
    return div.innerHTML;
  };

const getDocument = () => document;

export const htmlToJson = htmlToJsonFactory(getDocument, (html: string) => {
  const { body } = new DOMParser().parseFromString(html, 'text/html');
  return body.firstChild!;
});

export const jsonToHtml = jsonToHtmlFactory(getDocument);
